import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { businessSelectors, businessTypes } from 'app-components/flux/business';
import type { Business } from 'app-components/flux/business/businessTypes';
import BusinessEditDialog from 'components/Dialogs/BusinessEdit';
import Dump from 'components/Dump';
import { useAppSelector } from 'hooks/redux';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import BusinessIcon from './BusinessIcon';
import BusinessThreeDot from './BusinessThreeDot';

const useStyles = makeStyles()((theme) => ({
  businessCard: {
    padding: theme.spacing(2),
    width: '100%',
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  businessCardGSF: {
    extends: 'businessCard',
    padding: theme.spacing(2),
    width: '100%',
    margin: '6px 2px',
    marginBottom: '6px',
  },
  firstBox: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addressPadding: {
    paddingRight: 4,
  },
  addressContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    '& > div:nth-child(2)': {
      justifySelf: 'center',
    },
    '& > div:nth-child(3)': {
      justifySelf: 'flex-end',
    },
  },
}));

function BusinessCard({
  business,
  isGSF = false,
  hideMenu = false,
}: {
  business: Business;
  isGSF?: boolean;
  hideMenu?: boolean;
}) {
  const { classes } = useStyles();
  const [editBusiness, setEditBusiness] = React.useState(false);
  const { address, id } = business;
  const defaultBusiness = useAppSelector(businessSelectors.getDefaultBusiness);

  return (
    <>
      {editBusiness && !isGSF && (
        <BusinessEditDialog onClose={() => setEditBusiness(false)} initialBusiness={business} />
      )}
      <Paper
        className={isGSF ? classes.businessCardGSF : classes.businessCard}
        key={`business-card-${business.id}`}
        id={`business-card-${business.id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setEditBusiness(true);
        }}
      >
        <Box className={classes.firstBox}>
          <Box display='flex' position='relative' sx={{ opacity: business?.isClosed ? 0.5 : 1 }}>
            <Dump obj={business} />
            <BusinessIcon business={business} />
            <Box display='flex' flexDirection='column'>
              <Typography variant='titleMedium'>{business.name}</Typography>
              <Typography variant='captionRegular' color={'textSecondary'}>
                {/*
                // @ts-expect-error FIXME TS7053 */}
                {`${businessTypes.industryObj[business.industry]} • ${
                  // @ts-expect-error FIXME TS7053
                  businessTypes.businessStructureObj[business.structure]
                }`}
              </Typography>
            </Box>
          </Box>
          {!hideMenu && (
            <Box display='flex' alignItems='center' gap={1}>
              {business?.isClosed && <Chip variant='tonal' size='medium' color='default' label='Closed' />}

              {defaultBusiness?.id === id && <Chip variant='tonal' size='medium' color='info' label='Primary' />}
              <BusinessThreeDot business={business} setEditBusiness={setEditBusiness} />
            </Box>
          )}
        </Box>
        {!isGSF && (
          <Box mx='56px' justifyContent='space-between' className={classes.addressContainer}>
            {address?.line1 && !business.isClosed && (
              <Box display='flex' flexDirection='column' mt={2} sx={{ opacity: business?.isClosed ? 0.5 : 1 }}>
                <Typography color='textSecondary' variant='captionRegular'>
                  Address
                </Typography>
                {address.line1 && <Typography variant='captionRegular'>{address.line1}</Typography>}
                {address.line2 && <Typography variant='captionRegular'>{address.line2}</Typography>}
                <Box display='flex'>
                  {address.city && (
                    <Typography
                      variant='captionRegular'
                      className={classes.addressPadding}
                    >{`${address.city}, `}</Typography>
                  )}
                  {address.state && (
                    <Typography variant='captionRegular' className={classes.addressPadding}>
                      {address.state}
                    </Typography>
                  )}
                  {address.zipCode && <Typography variant='captionRegular'>{address.zipCode}</Typography>}
                </Box>
              </Box>
            )}
            {business.email && (
              <Box display='flex' flexDirection='column' mt={2} sx={{ opacity: business?.isClosed ? 0.5 : 1 }}>
                <Typography color='textSecondary' variant='captionRegular'>
                  Email
                </Typography>
                <Typography variant='captionRegular'>{business.email}</Typography>
              </Box>
            )}
            {business.phoneNumber && (
              <Box display='flex' flexDirection='column' mt={2} sx={{ opacity: business?.isClosed ? 0.5 : 1 }}>
                <Typography color='textSecondary' variant='captionRegular'>
                  Phone
                </Typography>
                <Typography variant='captionRegular'>{business.phoneNumber}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </>
  );
}

export default React.memo(BusinessCard);
